import {memo, type CSSProperties, type ReactNode} from "react";
import {token} from "@atlaskit/tokens";
import type {BeatPlanningCalendarEvent, EventColorMap} from "~/components/beatPlanningCalendar/beatPlanningCalendar";
import {formatTimeFromJsDate} from "~/utilities/utilities";

type CalendarSelectableEvent = {
    id: string;
    title: string;
    start: Date | null;
    end: Date | null;
    allDay: boolean;
    extendedProps: unknown;
    backgroundColor?: string;
    borderColor?: string;
    textColor?: string;
};

export type CalendarScheduleGroup = {
    dayKey: string;
    dayNumber: string;
    dayMeta: string;
    events: Array<{
        key: string;
        event: BeatPlanningCalendarEvent;
        backgroundColor?: string;
        borderColor?: string;
        textColor?: string;
        start: Date;
        end: Date;
    }>;
};

function allDayStatusChipOnGreenTileStyle(
    statusType: string | undefined,
    getEventColor: ((type: string | undefined) => EventColorMap) | undefined,
): CSSProperties | undefined {
    if (statusType === undefined || getEventColor === undefined) {
        return undefined;
    }
    const colors = getEventColor(statusType);
    if (
        colors.backgroundColor === undefined &&
        colors.borderColor === undefined &&
        colors.textColor === undefined
    ) {
        return undefined;
    }
    return {
        backgroundColor: colors.backgroundColor ?? token("color.background.neutral"),
        borderColor: colors.borderColor ?? token("color.border"),
        color: colors.textColor ?? token("color.text"),
    };
}

function MemoizedCalendarScheduleList({
    scheduleGroups,
    allDayStyle,
    startTimeOnly,
    getEventColor,
    renderEmptyState,
    isScheduleDataLoading = false,
    hasMore = false,
    isLoadingMore = false,
    loadMoreError = null,
    onLoadMore,
    onEventSelected,
    eventOpensDaySummaryModal = false,
    onOpenDaySummaryForEventStart,
}: {
    scheduleGroups: Array<CalendarScheduleGroup>;
    allDayStyle: {mode: "fixedGreen" | "inheritStatus"};
    startTimeOnly: boolean;
    getEventColor: ((type: string | undefined) => EventColorMap) | undefined;
    renderEmptyState: () => ReactNode;
    /** When true, suppress the empty state while rows are loading (e.g. after search/filter). */
    isScheduleDataLoading?: boolean;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    loadMoreError?: string | null;
    onLoadMore?: () => void;
    onEventSelected: (event: CalendarSelectableEvent) => void;
    eventOpensDaySummaryModal?: boolean;
    onOpenDaySummaryForEventStart?: (start: Date) => void;
}) {
    return (
        <div className="beat-planning-calendar__schedule-list">
            {scheduleGroups.length === 0 && isScheduleDataLoading === false ? (
                renderEmptyState()
            ) : scheduleGroups.length === 0 ? null : (
                scheduleGroups.map((group) => (
                    <div
                        key={group.dayKey}
                        className="beat-planning-calendar__schedule-group"
                    >
                        <div className="beat-planning-calendar__schedule-group-date-col">
                            <span className="beat-planning-calendar__schedule-group-date">{group.dayNumber}</span>
                        </div>
                        <div className="beat-planning-calendar__schedule-group-meta-col">
                            <span className="beat-planning-calendar__schedule-group-meta">{group.dayMeta}</span>
                        </div>
                        <div className="beat-planning-calendar__schedule-group-events">
                            {group.events.map((item) => {
                                const extendedProps = item.event.extendedProps as
                                    | {
                                          typeLabel?: string;
                                          templateLabel?: string;
                                          templateStrokeColor?: string;
                                          rawRow?: {
                                              btlActivityTemplateName?: string;
                                          };
                                      }
                                    | undefined;
                                const statusType = item.event.type;
                                const statusLabel = extendedProps?.typeLabel;
                                const templateLabel =
                                    extendedProps?.templateLabel ?? extendedProps?.rawRow?.btlActivityTemplateName;
                                const templateStrokeColor = extendedProps?.templateStrokeColor;
                                const isAllDayEvent = item.event.allDay === true;
                                const allDayStatusChipStyle =
                                    isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                        ? allDayStatusChipOnGreenTileStyle(statusType, getEventColor)
                                        : undefined;
                                const cardBackgroundColor =
                                    isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                        ? token("color.icon.accent.green")
                                        : item.backgroundColor;
                                const cardTextColor =
                                    isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                        ? token("color.text.inverse")
                                        : item.textColor;
                                const cardBorderColor =
                                    isAllDayEvent === true && allDayStyle.mode === "fixedGreen"
                                        ? token("color.icon.accent.green")
                                        : item.borderColor;
                                const startTimeStr = formatTimeFromJsDate(item.start);
                                const endTimeStr = formatTimeFromJsDate(item.end);

                                const timeLabel =
                                    isAllDayEvent === true
                                        ? "All day"
                                        : startTimeOnly === true
                                          ? startTimeStr
                                          : `${startTimeStr} - ${endTimeStr}`;
                                return (
                                    <button
                                        key={item.key}
                                        type="button"
                                        className="beat-planning-calendar__schedule-event-widget"
                                        onClick={() => {
                                            if (
                                                eventOpensDaySummaryModal === true &&
                                                onOpenDaySummaryForEventStart !== undefined
                                            ) {
                                                onOpenDaySummaryForEventStart(item.start);
                                                return;
                                            }
                                            onEventSelected({
                                                id: item.event.id,
                                                title: item.event.title,
                                                start: item.start,
                                                end: item.end,
                                                allDay: item.event.allDay ?? false,
                                                extendedProps: item.event.extendedProps,
                                                backgroundColor: item.backgroundColor,
                                                borderColor: item.borderColor,
                                                textColor: item.textColor,
                                            });
                                        }}
                                        style={{
                                            backgroundColor: cardBackgroundColor,
                                            color: cardTextColor,
                                            borderColor: cardBorderColor,
                                        }}
                                    >
                                        <div className="beat-planning-calendar__schedule-event-main">
                                            <span className="beat-planning-calendar__schedule-event-time">{timeLabel}</span>
                                            <span>&bull;</span>
                                            <span className="beat-planning-calendar__schedule-event-title">{item.event.title}</span>
                                        </div>
                                        <div className="beat-planning-calendar__schedule-event-chips">
                                            {isAllDayEvent === true &&
                                            statusLabel !== undefined &&
                                            allDayStyle.mode === "fixedGreen" ? (
                                                <span
                                                    className="beat-planning-calendar__schedule-event-chip"
                                                    style={allDayStatusChipStyle}
                                                >
                                                    {statusLabel}
                                                </span>
                                            ) : null}
                                            {templateLabel !== undefined ? (
                                                <span
                                                    className="beat-planning-calendar__schedule-event-chip"
                                                    style={{borderColor: templateStrokeColor}}
                                                >
                                                    {templateLabel}
                                                </span>
                                            ) : null}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
            {loadMoreError !== null ? (
                <div role="alert" className="p-3 text-center text-sm">
                    <div>{loadMoreError}</div>
                    {onLoadMore === undefined ? null : (
                        <button type="button" className="mt-2 underline" onClick={onLoadMore}>Retry</button>
                    )}
                </div>
            ) : null}
            {hasMore === true && loadMoreError === null && onLoadMore !== undefined ? (
                <div className="p-3 text-center">
                    <button type="button" className="underline" disabled={isLoadingMore} onClick={onLoadMore}>
                        {isLoadingMore === true ? "Loading more…" : "Load more"}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

export const CalendarScheduleList = memo(MemoizedCalendarScheduleList);
